/******/ (() => { // webpackBootstrap
/*!****************************************!*\
  !*** ./js/sections/contact-us-form.js ***!
  \****************************************/
/**
 * Contact Us page — reveal-card "collapse the rest of the form" behaviour (template-scoped).
 * Built to assets/contact-us-form.js.
 *
 * Loaded only on `page.template_suffix == 'contact-us'` (see layout/theme.liquid),
 * so this behaviour never ships in the site-wide global.js bundle.
 *
 * Behaviour: when a [data-hides-submit] reveal card becomes visible, every form
 * field positioned AFTER the card's row inside [data-form-content] is hidden —
 * including the submit button. The form collapses to: intro + the controlling
 * select + the revealed cards. When no such card is visible, the hidden fields
 * are restored.
 *
 * A card's visibility is owned by its linked form-row ancestor: form-validation.js
 * toggles `tw-hidden` on that row. This script observes the document for any class
 * change and re-queries the DOM each pass — so it is immune to re-render/hydration
 * that replaces form nodes (binding an observer to a specific node would be orphaned
 * when that node is swapped). It restores only the elements it hid (tracked via
 * data-reveal-collapsed), never touching elements hidden by other logic.
 */

var CONTENT_SELECTOR = '[data-form-content]';
var HIDE_CARD_SELECTOR = '[data-hides-submit]';
var LINKED_ROW_SELECTOR = '[data-linked-form-element-name]';
var COLLAPSED_FLAG = 'data-reveal-collapsed';

/**
 * A reveal card is visible when its linked form-row ancestor is not hidden.
 * @param {Element} card - a [data-hides-submit] element
 * @returns {boolean}
 */
var isCardVisible = card => {
  var row = card.closest(LINKED_ROW_SELECTOR);
  // No linked row means the card has no reveal gate, so it can never "become
  // visible" on a selection — treat as not-collapsing to avoid permanently
  // hiding the form on a misconfigured (ungated) card.
  if (!row) return false;
  return !row.classList.contains('tw-hidden');
};

/**
 * Index of the last [data-form-content] child that contains a currently-visible
 * hide card. -1 when no such card is visible.
 * @param {Element[]} children
 * @returns {number}
 */
var lastVisibleCardIndex = children => children.reduce((last, child, index) => {
  var card = child.querySelector(HIDE_CARD_SELECTOR);
  return card && isCardVisible(card) ? index : last;
}, -1);

/**
 * Collapse (or restore) every field after the card row of one content container.
 * @param {Element} content - a [data-form-content] container
 */
var syncForm = content => {
  var children = Array.from(content.children);
  var cardIndex = lastVisibleCardIndex(children);
  var shouldCollapse = cardIndex >= 0;
  children.forEach((child, index) => {
    if (index <= cardIndex) return; // keep the cards and everything before them

    if (shouldCollapse) {
      if (!child.classList.contains('tw-hidden')) {
        child.classList.add('tw-hidden');
        child.setAttribute(COLLAPSED_FLAG, '');
      }
    } else if (child.hasAttribute(COLLAPSED_FLAG)) {
      child.classList.remove('tw-hidden');
      child.removeAttribute(COLLAPSED_FLAG);
    }
  });
};

/**
 * Re-query and sync every form on the page. Reference-free, so it always operates
 * on the live DOM even after a re-render swaps nodes.
 */
var syncAll = () => {
  document.querySelectorAll(CONTENT_SELECTOR).forEach(content => {
    if (content.querySelector(HIDE_CARD_SELECTOR)) syncForm(content);
  });
};

// Coalesce bursts of mutations (including our own class changes) into one pass
// per frame. syncForm is idempotent, so the follow-up pass settles with no churn.
var scheduled = false;
var schedule = () => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    try {
      syncAll();
    } catch (error) {
      console.error('[contact-us] reveal-card form collapse failed:', error);
    }
  });
};
var init = () => {
  // Any class toggle (the card-row reveal) or node insertion (re-render) anywhere
  // re-triggers a sync. Observing the document avoids holding orphan-able refs.
  var observer = new MutationObserver(schedule);
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    subtree: true,
    childList: true
  });

  // Initial passes — cover a pre-selected/restored value and late hydration.
  // (No change/input listeners needed: form-validation.js always toggles a class
  // in response to those events, which the observer above already catches.)
  schedule();
  window.addEventListener('load', schedule);
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
/******/ })()
;